import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MvLandscape } from './brand/MvLandscape';
import ArticleSearchResults, {
  SearchConversation,
  submitSearch,
  useArticleSearch,
  useSearchConversation,
} from './ArticleSearchResults';

/**
 * The knowledge assistant, drawn as the header pulled down into a sheet: the
 * white header continues into the panel through curved shoulders, and the MV
 * landscape that edges the header moves to the sheet's bottom edge while it
 * is open (see "AI assistant sheet" in index.css).
 */
function AiSearch({ isOpen, onClose, request }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const search = useArticleSearch(query);
  const [exchanges, chat] = useSearchConversation();
  // While a query with hits is typed, the hits take the chat's place above
  // the field; with none (yet) the chat stays, greeting first.
  const isSearching = query.trim() !== ''
    && (search.results.length > 0 || search.tags.length > 0);

  // A page handing over what was typed in its own field (see useOpenAiSearch):
  // continue right there, caret at the end.
  useEffect(() => {
    if (!request?.query) return;
    setQuery(request.query);
    const input = inputRef.current;
    if (input) {
      input.focus();
      const end = request.query.length;
      window.requestAnimationFrame(() => input.setSelectionRange(end, end));
    }
  }, [request]);

  useEffect(() => {
    document.body.classList.toggle('ai-open', isOpen);
    if (isOpen) window.setTimeout(() => inputRef.current?.focus(), 250);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('ai-open');
    };
  }, [isOpen, onClose]);

  const submit = (event) => {
    event.preventDefault();
    // A scenario message, or a query nothing matched, becomes a chat message;
    // otherwise Enter opens the best hit.
    const asked = submitSearch(query, search, (path) => {
      onClose();
      navigate(path);
    }, chat);

    if (asked) setQuery('');
  };

  return (
    <div className={`ai-overlay${isOpen ? ' open' : ''}`} aria-hidden={!isOpen} onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="ai-shell">
        <section className={`ai-panel${isSearching ? ' ai-panel--searching' : ''}`} role="dialog" aria-modal="true" aria-labelledby="ai-panel-title">
          <div className="ai-panel-header">
            {/* The chat gets the room; the title stays for screen readers. */}
            <h2 id="ai-panel-title" className="sr-only">{t('aiSearch.title')}</h2>

            <button type="button" className="ai-panel-close" onClick={onClose} aria-label={t('aiSearch.close')}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {isSearching ? (
            <ArticleSearchResults
              query={query}
              status={search.status}
              results={search.results}
              tags={search.tags}
              onNavigate={onClose}
              className="ai-search-results"
            />
          ) : (
            <div className="ai-chat-history" aria-label={t('aiSearch.chatHistoryAriaLabel')}>
              <div className="ai-message ai-message--assistant">
                <strong>{t('aiSearch.assistantName')}</strong>
                <p>{t('aiSearch.welcome')}</p>
              </div>
              <SearchConversation exchanges={exchanges} onNavigate={onClose} />
            </div>
          )}

          <form className="ai-search-large" onSubmit={submit}>
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('aiSearch.searchPlaceholder')}
              aria-label={t('aiSearch.searchPlaceholder')}
            />
            <button type="submit" className="ai-search-submit">
              <span>{t('aiSearch.submit')}</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </form>
        </section>

        <MvLandscape
          className="ai-shell-landscape"
          bands={[
            { color: 'var(--mv-eisblau-50)', top: 6, amplitude: 44 },
            { color: 'var(--mv-himmelblau)', top: 42, amplitude: 36, flip: true },
            { color: 'var(--mv-wasserblau)', top: 64, amplitude: 28 },
          ]}
        />
      </div>
    </div>
  );
}

export default AiSearch;
